import { ChangeDetectorRef, Component, Input, OnInit, ViewChild,
         ViewContainerRef
       } from '@angular/core';

import { Severity } from 'core/logging/constants';
import { LoggingService } from 'core/logging/logging.service';
import { Utils } from 'core/utils';
import { AdditionalInfoItemInterface
       } from './additional-info-item-components/additional-info-item-interface'; // eslint-disable-line
import { AdditionalInfoItemConfig } from './collage-designer-config';

@Component({
    selector: 'additional-info-items',
    templateUrl: './additional-info-items.component.html',
    styleUrls: ['./additional-info-items.component.scss'],
    standalone: false
})
export class AdditionalInfoItemsComponent implements OnInit
{
  @Input() public additionalInfoItems: Array<AdditionalInfoItemConfig> = [];

  public trackByLabel: (index: number, item: any) => any = Utils.trackByLabel;
  public visibleComponents: Array<string> = [];

  @ViewChild('spinner') public spinner: any;
  @ViewChild('loadedComponent', {read: ViewContainerRef})
      public loadedComponent: ViewContainerRef;
  @ViewChild('componentsContainer', {read: ViewContainerRef, static: true})
      public componentsContainer: ViewContainerRef;

  constructor(
      private cdr: ChangeDetectorRef,
      private loggingService: LoggingService)
  {
  }

  public async ngOnInit(): Promise<void>
  {
    for (const additionalInfoItem of this.additionalInfoItems) {
      if (!additionalInfoItem.location) {
        continue;
      }

      const componentPathName = this.getComponentPathName(additionalInfoItem);
      const component = await this.loadComponent(
          componentPathName,
          additionalInfoItem.label,
          this.componentsContainer);
      if (additionalInfoItem.autoDisplay && Utils.isLargeDevice()) {
        this.loadContent(additionalInfoItem);
        additionalInfoItem.autoDisplay = false;
        this.visibleComponents.push(additionalInfoItem.label);
      }
    }
  }

  private getComponentPathName(
      additionalInfoItem: AdditionalInfoItemConfig): string
  {
    const componentMatch = additionalInfoItem.location.match(
        /.*productdesigner:(.+)/);
    if (componentMatch && componentMatch.length === 2) {
      return componentMatch[1];
    }
    else {
      return 'external-html-content';
    }
  }

  public async onTriggerClicked(label: string): Promise<void>
  {
    const additionalInfoItem = Utils.find(
        this.additionalInfoItems, item => item.label === label);
    if (!additionalInfoItem.location) {
      return;
    }

    if (this.visibleComponents.includes(label)) {
      this.removeComponent(label);
      return;
    }

    this.visibleComponents.push(label);
    if (!Utils.isLargeDevice()) {
      Utils.scrollToPosition(0, 0);
    }
    this.cdr.detectChanges();
    await this.loadContent(additionalInfoItem);
  }

  private async loadContent(
      additionalInfoItem: AdditionalInfoItemConfig): Promise<void>
  {
    const componentPathName = this.getComponentPathName(additionalInfoItem);
    try {
      const component = await this.loadComponent(
          componentPathName,
          additionalInfoItem.label,
          this.loadedComponent);
      await component.initComponent(
          additionalInfoItem,
          this.componentsContainer.element.nativeElement.getRootNode());
    }
    catch (error) {
      this.loggingService.log(
          'AdditionalInfoItemsComponent',
          Severity.ERROR,
          '[ALERT] failed to load additional info item: ' + error.toString());
      this.removeComponent(additionalInfoItem.label);
    }
  }

  private async loadComponent(
      componentPathName: string,
      label: string,
      container: ViewContainerRef
      ): Promise<AdditionalInfoItemInterface>
  {
    const componentObject: any = await import(
        `./additional-info-item-components/${componentPathName}.component.ts`);
    const componentName = Object.getOwnPropertyNames(componentObject)[0];

    const component = <AdditionalInfoItemInterface>container
      .createComponent(componentObject[componentName])
      .instance;
    component.removeComponent = () => this.removeComponent(label);
    return component;
  }

  public removeComponent(label: string): void
  {
    this.visibleComponents.splice(this.visibleComponents.indexOf(label), 1);
    this.loadedComponent.clear();
  }
}
